import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, KeyRound, LockKeyhole, Plus, ShieldCheck, ShieldOff } from 'lucide-react';
import { Badge, CopyButton, EmptyState, Field, Modal, PageHeader } from '../components/ui';
import { ReadState } from '../components/ReadState';
import { ConsoleApiError, type KeySummary } from '../services/console';
import { messageFor, useReadResource, utcTime } from '../lib/live';
import { useConsole } from '../state/ConsoleProvider';

type KeyDialog = { type: 'create' } | { type: 'revoke'; key: KeySummary } | null;
const PAGE_SIZE = 20;

export default function ApiKeys() {
  const { api, notify } = useConsole();
  const [page, setPage] = useState(1);
  const loadKeys = useCallback(() => api.listKeys(page, PAGE_SIZE), [api, page]);
  const keys = useReadResource(loadKeys, 'Your API keys could not be loaded. Try again.');
  const [dialog, setDialog] = useState<KeyDialog>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [generated, setGenerated] = useState<{ name: string; secret: string } | null>(null);
  const current = useRef(true);
  const mutationInFlight = useRef(false);
  useEffect(() => { current.current = true; return () => { current.current = false; }; }, []);
  const nameBytes = new TextEncoder().encode(name.trim()).byteLength;

  function openCreate() {
    setName('');
    setError('');
    setUncertain(false);
    setDialog({ type: 'create' });
  }

  function closeDialog() {
    if (mutationInFlight.current) return;
    setDialog(null);
    setName('');
    setError('');
    setUncertain(false);
  }

  function refreshKeys() {
    setPage(1);
    keys.retry();
  }

  function checkKeys() {
    closeDialog();
    refreshKeys();
  }

  async function createKey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (dialog?.type !== 'create' || mutationInFlight.current || uncertain) return;
    const cleanName = name.trim();
    if (!cleanName || new TextEncoder().encode(cleanName).byteLength > 50 || /[\u0000-\u001f\u007f]/.test(cleanName)) {
      setError('Enter a key name of up to 50 bytes, without control characters.');
      return;
    }
    mutationInFlight.current = true;
    setPending(true);
    setError('');
    try {
      const result = await api.createKey(cleanName);
      if (!current.current) return;
      // Save the one-time secret before any list read, including a failed refresh.
      setGenerated({ name: result.key.name, secret: result.secret });
      setDialog(null);
      setName('');
      refreshKeys();
    } catch (cause) {
      if (current.current) {
        setError(messageFor(cause, 'The key could not be created. Check your key list before trying again.'));
        setUncertain(!(cause instanceof ConsoleApiError) || cause.mutationUncertain);
      }
    } finally {
      mutationInFlight.current = false;
      if (current.current) setPending(false);
    }
  }

  async function revokeKey() {
    if (dialog?.type !== 'revoke' || mutationInFlight.current || uncertain) return;
    mutationInFlight.current = true;
    setPending(true);
    setError('');
    try {
      await api.revokeKey(dialog.key.id);
      if (!current.current) return;
      setDialog(null);
      notify('API key revoked.', 'success');
      refreshKeys();
    } catch (cause) {
      if (current.current) {
        setError(messageFor(cause, 'The key could not be revoked. Check your key list before trying again.'));
        setUncertain(!(cause instanceof ConsoleApiError) || cause.mutationUncertain);
      }
    } finally {
      mutationInFlight.current = false;
      if (current.current) setPending(false);
    }
  }

  return <>
    <PageHeader eyebrow="YOUR ROTEN ACCOUNT" title="API keys" description="Create a key for each application. A full key is shown only once, when you create it." actions={<><button className="button button-secondary" onClick={keys.retry} disabled={keys.loading}>Refresh keys</button><button className="button button-primary" onClick={openCreate} disabled={pending}><Plus size={16} />Create API key</button></>} />

    <section className="panel management-key-panel" aria-labelledby="key-list-heading">
      <div className="panel-header"><div><h2 id="key-list-heading">Your API keys {keys.data && <span className="management-count">{keys.data.total}</span>}</h2><p>Existing keys are shown as masked identifiers.</p></div><span className="management-header-note"><LockKeyhole size={14} />Full keys are shown once</span></div>
      <ReadState {...keys} label="Loading API keys…" />
      {keys.data && <>
        {keys.data.items.length === 0 ? <EmptyState icon={KeyRound} title={keys.data.total ? 'No keys on this page' : 'Create your first API key'} description={keys.data.total ? 'Return to the previous page to see your keys.' : 'Choose a name that helps you recognize the application using it.'} action={!keys.data.total && <button className="button button-primary" onClick={openCreate}><Plus size={16} />Create API key</button>} /> : <div className="table-wrap"><table className="management-key-table">
          <caption className="sr-only">Roten API keys</caption>
          <thead><tr><th scope="col">Name</th><th scope="col">Key identifier</th><th scope="col">Created (UTC)</th><th scope="col">Last used (UTC)</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{keys.data.items.map(key => <tr key={key.id}>
            <td><div className="management-key-name"><span className="management-key-icon"><KeyRound size={16} /></span><span>{key.name}</span></div></td>
            <td><code className="mono small-text nowrap">{key.maskedKey}</code></td>
            <td className="management-date">{utcTime(key.createdAt)}</td>
            <td className="management-date">{key.lastUsedAt > 0 ? utcTime(key.lastUsedAt) : 'Never used'}</td>
            <td><Badge tone={key.status === 1 ? 'success' : 'neutral'}>{key.status === 1 ? 'Active' : 'Inactive'}</Badge></td>
            <td><div className="management-row-actions"><button className="button button-ghost management-danger-action" aria-label={`Revoke ${key.name}`} disabled={pending} onClick={() => { setError(''); setUncertain(false); setDialog({ type: 'revoke', key }); }}><ShieldOff size={15} />Revoke</button></div></td>
          </tr>)}</tbody>
        </table></div>}
        <div className="pagination"><span>{keys.data.total.toLocaleString('en-US')} keys · Page {keys.data.page}</span><div><button className="button button-secondary" disabled={keys.data.page <= 1} onClick={() => setPage(Math.max(1, keys.data!.page - 1))}>Previous</button><button className="button button-secondary" disabled={keys.data.page * keys.data.pageSize >= keys.data.total} onClick={() => setPage(keys.data!.page + 1)}>Next</button></div></div>
      </>}
    </section>

    <div className="management-help-grid">
      <section className="panel management-help-card"><ShieldCheck size={20} /><div><h3>Keep your keys private</h3><p>Store your keys on your server. Revoke a key if it is exposed or an application no longer needs it.</p></div></section>
      <section className="panel management-help-card"><KeyRound size={20} /><div><h3>Follow your usage</h3><p>Use a distinct name for each application so you can find its consumption history.</p><Link className="text-link" to="/usage">View usage <ArrowUpRight size={14} /></Link></div></section>
    </div>

    {dialog?.type === 'create' && <Modal title="Create API key" description="Choose a name that helps you recognize where this key will be used." onClose={closeDialog}>
      <form className="form-stack" onSubmit={createKey}>
        <Field label="Key name" hint={`Up to 50 bytes. Non-English characters may use more than one byte. ${nameBytes}/50 bytes used.`}><input className="input" value={name} onChange={event => setName(event.target.value)} placeholder="Production server" maxLength={50} required autoFocus disabled={pending || uncertain} aria-invalid={Boolean(error) || nameBytes > 50} /></Field>
        <p className="management-form-note">You will see the full key once. Save it somewhere secure before closing the next dialog.</p>
        {error && <p className="management-form-error" role="alert">{error}</p>}
        <div className="management-dialog-actions"><button type="button" className="button button-secondary" disabled={pending} onClick={closeDialog}>Cancel</button>{uncertain ? <button type="button" className="button button-primary" onClick={checkKeys}>Check key list</button> : <button className="button button-primary" type="submit" disabled={pending || !name.trim() || nameBytes > 50}>{pending ? 'Creating…' : 'Create key'}</button>}</div>
      </form>
    </Modal>}

    {dialog?.type === 'revoke' && <Modal title="Revoke API key?" description={`“${dialog.key.name}” will stop working. This cannot be undone. Applications using it will need a new key.`} onClose={closeDialog}>
      <div className="management-revoke-summary"><KeyRound size={18} /><code className="mono">{dialog.key.maskedKey}</code></div>
      {error && <p className="management-form-error" role="alert">{error}</p>}
      <div className="management-dialog-actions"><button className="button button-secondary" disabled={pending} onClick={closeDialog}>Keep key</button>{uncertain ? <button className="button button-primary" onClick={checkKeys}>Check key list</button> : <button className="button management-button-danger" disabled={pending} onClick={() => void revokeKey()}>{pending ? 'Revoking…' : 'Revoke key'}</button>}</div>
    </Modal>}

    {generated && <Modal title="Save your API key" description={`Your key “${generated.name}” is ready. Copy it now; it will not be shown again after you close this dialog.`} onClose={() => setGenerated(null)}>
      <div className="management-secret-label"><Check size={16} /><span>Key created</span></div>
      <div className="management-secret"><code className="mono">{generated.secret}</code><CopyButton value={generated.secret} label="Copy key" /></div>
      <div className="management-notice management-secret-notice"><LockKeyhole size={17} /><p>Keep this key private. Roten will show only its masked identifier after you close this dialog.</p></div>
      <div className="management-dialog-actions"><button className="button button-primary" onClick={() => setGenerated(null)}>I’ve saved my key</button></div>
    </Modal>}
  </>;
}
